import { useEffect, useRef, useState } from 'react';

import { formatHours, NOTE_MAX_LENGTH } from '@time-tracking/shared';

import { useFocusTrap } from '../hooks/useFocusTrap.js';
import type { Recap } from '../utils/recap.js';

const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent || '');
const SUBMIT_HINT = IS_MAC ? '⌘↵ to submit' : 'Ctrl+↵ to submit';

interface SubmitDialogProps {
  open: boolean;
  weekRange: string;
  recap: Recap;
  /** The last submission's note, pre-filled when re-submitting a recalled sheet. */
  initialNote?: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}

export function SubmitDialog({
  open,
  weekRange,
  recap,
  initialNote = '',
  isSubmitting,
  onCancel,
  onConfirm,
}: SubmitDialogProps) {
  const [note, setNote] = useState(initialNote);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  useFocusTrap(dialogRef, open);

  // Seed with the previous submission's note ONLY on the open transition — not on
  // every re-render — so a re-render while the dialog is open (e.g. autosave or a
  // background refetch changing the parent) never wipes what the user is typing.
  useEffect(() => {
    if (open && !wasOpen.current) {
      setNote(initialNote);
      textareaRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open, initialNote]);

  // Esc closes while the dialog is open — but not mid-submit, so an in-flight
  // request can't look like it was canceled.
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isSubmitting) onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onCancel, isSubmitting]);

  if (!open) return null;

  function confirm() {
    if (isSubmitting) return;
    onConfirm(note.trim());
  }

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        // Ignore backdrop clicks mid-submit so it can't look like a cancel.
        if (e.target === e.currentTarget && !isSubmitting) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="submit-dialog-title"
        className="submit-dialog"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="submit-dialog__head">
          <h2 id="submit-dialog-title" className="submit-dialog__title">
            Submit for approval?
          </h2>
          <p className="submit-dialog__sub">
            Your hours for <strong>{weekRange}</strong> go to your approver. You can recall the
            sheet if you need to change something before they sign off.
          </p>
        </div>

        <div className="submit-dialog__recap">
          <ul className="recap-ledger">
            {recap.rows.map((r) => (
              <li key={r.key} className={`recap-line${r.indirect ? ' indirect' : ''}`}>
                <span className="recap-line__label">{r.label}</span>
                <span className="recap-line__hrs">{formatHours(r.minutes)}h</span>
              </li>
            ))}
            <li className="recap-line recap-line--total">
              <span className="recap-line__label">Total</span>
              <span className="recap-line__hrs">{formatHours(recap.totalMinutes)}h</span>
            </li>
          </ul>
        </div>

        <div className="submit-dialog__note">
          <label htmlFor="submit-note">
            Add a note for your approver <span className="submit-dialog__optional">(optional)</span>
          </label>
          <textarea
            ref={textareaRef}
            id="submit-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                confirm();
              }
            }}
            maxLength={NOTE_MAX_LENGTH}
            rows={3}
            placeholder="Anything your approver should know about this week — overtime, a short week, hours moved between projects…"
            aria-describedby="submit-note-count"
          />
          {/* Described-by the textarea (read on focus); no aria-live so it
              doesn't announce on every keystroke. */}
          <div id="submit-note-count" className="submit-dialog__count">
            {note.length}/{NOTE_MAX_LENGTH}
          </div>
        </div>

        <div className="submit-dialog__foot">
          <span className="submit-dialog__hint" aria-hidden="true">
            {SUBMIT_HINT}
          </span>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={confirm}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}
